import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button.tsx'
import { DownloadQuality } from '@/models/downloadOptions.ts'

interface DownloadQualityOptionsProps {
  /** Qualities that may be offered — always resolve them via `resolveDownloadQualities()`. */
  qualities: DownloadQuality[]
  onSelect: (quality: DownloadQuality) => void
  /** Per-quality disabled state, e.g. when the download would exceed the size limit. */
  disabled?: Partial<Record<DownloadQuality, boolean>>
  /** Rendered next to the button, e.g. the estimated size. */
  hint?: Partial<Record<DownloadQuality, ReactNode>>
  /** Rendered below the option, e.g. an error message. */
  note?: Partial<Record<DownloadQuality, ReactNode>>
  /** Stretch the buttons across the modal (wizard steps). */
  fullWidth?: boolean
}

/**
 * The resolution picker shared by every download dialog. Which options exist is the
 * caller's decision (`resolveDownloadQualities()`); this component only renders them, so
 * labels and order stay identical everywhere and a restriction can never be forgotten in
 * one of the dialogs.
 */
export default function DownloadQualityOptions({
  qualities,
  onSelect,
  disabled,
  hint,
  note,
  fullWidth,
}: DownloadQualityOptionsProps) {
  const { t } = useTranslation()

  const labels: Record<DownloadQuality, string> = {
    high: t('common.highResolution'),
    original: t('common.originalFiles'),
  }
  const descriptions: Partial<Record<DownloadQuality, string>> = {
    high: t('common.maxCompatibility'),
  }

  return (
    <div className={`flex flex-col gap-3 items-center ${fullWidth ? 'w-full' : ''}`}>
      {qualities.map((quality, index) => {
        const hintNode = hint?.[quality]
        const description = descriptions[quality]
        return (
          <div key={quality} className={`flex flex-col gap-1 items-center ${fullWidth ? 'w-full' : ''}`}>
            <div className={`flex gap-3 justify-center items-center ${fullWidth ? 'w-full' : ''}`}>
              <Button
                color='primary'
                // The first (recommended) option is solid, the rest outlined.
                variant={index === 0 ? undefined : 'outline'}
                className={fullWidth ? 'flex-1' : ''}
                disabled={disabled?.[quality]}
                onClick={() => onSelect(quality)}
              >
                {labels[quality]}
              </Button>
              {hintNode && <span className='text-sm whitespace-nowrap'>{hintNode}</span>}
            </div>
            {note?.[quality]}
            {description && <p className='text-sm opacity-50'>{description}</p>}
          </div>
        )
      })}
    </div>
  )
}
